import type { OKFUpload, OKFPreview, OKFRecord, OKFRevision, OKFNote, OKFContextRequest, OKFContext } from './okf'
import {
  AgentProgressEvent,
  AgentConfirmationRequiredFrame,
  ConfirmationResponsePayload,
  parseAgentServerFrame,
} from './agentProtocol'
import type {
  AgentAnswerContract,
  CanonicalEvidenceCitation,
  LegacyAnswerPreviewContract,
  LegacyBacklogItemContract,
  LegacyEvidenceClaimContract,
  LegacySystemMapEdgeContract,
  LegacySystemMapNodeContract,
} from '@aide/platform-types'
const API_BASE = '/api'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
  tool_calls?: ToolCall[]
}

export interface ToolCall {
  id: string
  function: {
    name: string
    arguments: string
  }
}

function normalizeEvidenceCitation(raw: RawEvidenceCitationData): CanonicalEvidenceCitation {
  return {
    citationId: raw.citation_id,
    evidenceRef: raw.evidence_ref,
    sourceSystem: raw.source_system,
    sourceType: raw.source_type,
    locator: raw.locator,
    storageMode: raw.storage_mode,
    contentHash: raw.content_hash,
    supportStatus: raw.support_status,
    reason: raw.reason,
  }
}

function isRawEvidenceCitation(value: unknown): value is RawEvidenceCitationData {
  return typeof value === 'object' && value !== null && 'citation_id' in value && 'evidence_ref' in value
}

function normalizeMaybeEvidenceCitation(value: unknown): CanonicalEvidenceCitation | null {
  if (isRawEvidenceCitation(value)) {
    return normalizeEvidenceCitation(value)
  }
  if (typeof value === 'object' && value !== null && 'citationId' in value && 'evidenceRef' in value) {
    return value as CanonicalEvidenceCitation
  }
  return null
}

function normalizeEvidenceCitations(values: unknown): CanonicalEvidenceCitation[] {
  return Array.isArray(values)
    ? values.map(normalizeMaybeEvidenceCitation).filter((citation): citation is CanonicalEvidenceCitation => citation !== null)
    : []
}

function normalizeLegacyAnswer(answer: RawLegacyAnswerPreviewData): LegacyAnswerPreviewData {
  return {
    ...answer,
    citations: (answer.citations || []).map(normalizeEvidenceCitation),
  }
}

function normalizeLegacyBacklogItem(item: RawLegacyBacklogItemData): LegacyBacklogItemData {
  return {
    ...item,
    citations: (item.citations || []).map(normalizeEvidenceCitation),
  }
}

function normalizeLegacyOverview(overview: RawLegacyUnderstandingOverviewData): LegacyUnderstandingOverviewData {
  return {
    ...overview,
    system_map_report: overview.system_map_report ? normalizeLegacySystemMapReport(overview.system_map_report) : null,
    answer_previews: overview.answer_previews.map(normalizeLegacyAnswer),
    modernization_backlog: overview.modernization_backlog.map(normalizeLegacyBacklogItem),
  }
}

function normalizeLegacyQuestionResponse(response: RawLegacyQuestionResponseData): LegacyQuestionResponseData {
  return {
    ...response,
    answer: normalizeLegacyAnswer(response.answer),
  }
}

function normalizeLegacyJourneyState(state: RawLegacyJourneyStateData): LegacyJourneyStateData {
  return {
    ...state,
    question_answer: state.question_answer ? normalizeLegacyAnswer(state.question_answer) : null,
  }
}

function normalizeLegacyEvidencePack(pack: RawLegacyEvidencePackData): LegacyEvidencePackData {
  return {
    ...pack,
    claims: pack.claims.map((claim) => ({
      ...claim,
      citations: (claim.citations || []).map(normalizeEvidenceCitation),
    })),
  }
}

function normalizeLegacySystemMapReport(report: RawLegacySystemMapReportData): LegacySystemMapReportData {
  return {
    ...report,
    nodes: report.nodes.map((node) => ({
      ...node,
      citations: (node.citations || []).map(normalizeEvidenceCitation),
    })),
    edges: report.edges.map((edge) => ({
      ...edge,
      citations: (edge.citations || []).map(normalizeEvidenceCitation),
    })),
  }
}

export interface ContextBundleFileRef {
  repo: string
  path: string
}

export interface ContextBundleExternalRef {
  id: string
  source: string
  title: string
}

export interface ContextBundle {
  file_refs: ContextBundleFileRef[]
  knowledge_refs: string[]
  external_refs: ContextBundleExternalRef[]
  query: string
}

export interface ChatResponse extends AgentAnswerContract {
  session_id: string
  response: string
  response_format?: 'markdown' | 'plain' | string
  render_hints?: Record<string, unknown>
  tool_calls?: ToolCall[]
  context_bundle?: ContextBundle | null
  citations: CanonicalEvidenceCitation[]
}

export interface ChatIntentContext {
  source: 'landing'
  persona_id: string
  action_id: string
  title?: string
}

export interface KnowledgeNodeData {
  node_id: string
  project_id: string
  tenant_id: string
  owner_user_id: string
  node_type: 'finding' | 'decision' | 'question' | 'insight' | 'code_annotation' | 'spec_section'
  title: string
  content: string
  status: 'draft' | 'promoted' | 'dismissed'
  confidence: number
  visibility: string
  source_type: 'auto_extracted' | 'user_selected'
  shared_with_id: string | null
  source_conversation_id: string | null
  source_message_id: string | null
  source_session_id: string | null
  tags: string[]
  created_at: string
  updated_at: string
}

export interface KnowledgeShareEventData {
  event_id: string
  node_id: string
  project_id: string
  tenant_id: string
  actor_user_id: string
  action: 'shared' | 'unshared'
  visibility: string
  shared_with_id: string | null
  shared_with_name: string | null
  previous_visibility: string | null
  created_at: string
}

export interface KnowledgeEdgeData {
  edge_id: string
  source_node_id: string
  target_node_id: string
  edge_type: 'supports' | 'contradicts' | 'refines' | 'derived_from' | 'relates_to' | 'sourced_from' | 'annotates' | 'linked_to' | 'affects' | 'mitigates' | 'governed_by' | 'realizes' | 'same_as'
  project_id: string
  tenant_id: string
  weight: number
  metadata: Record<string, unknown>
  created_at: string
}

export interface ProjectRepoLink {
  repo_name: string
  linked_at: string
  linked_by: string
}

export interface ProjectMember {
  user_id: string
  role: string
  added_at: string
  added_by: string
}

export interface ProjectData {
  project_id: string
  tenant_id: string
  org_id: string | null
  name: string
  description: string
  settings: Record<string, unknown>
  created_by: string
  created_at: string
  updated_at: string
  repositories?: ProjectRepoLink[]
  members?: ProjectMember[]
}

export interface LegacyKpiData {
  key: string
  label: string
  category: 'system' | 'data' | 'governance' | 'quality'
  value: number
  unit: string
  target: number
  status: 'pass' | 'warn' | 'fail'
  detail: string
}

export interface LegacyFormatSupportData {
  format: string
  support_level: 'full' | 'partial' | 'planned' | 'blocked'
  coverage: number
  notes: string
}

export interface LegacySourceSummaryData {
  source_id: string
  source_type: string
  name: string
  status: string
  coverage: number
  items_seen: number
  findings: string[]
}

export interface LegacyRiskFindingData {
  finding_id: string
  severity: 'info' | 'medium' | 'high' | 'critical'
  title: string
  detail: string
  owner: string
  remediation: string
}

export interface LegacyAnswerPreviewData extends LegacyAnswerPreviewContract {}

interface RawEvidenceCitationData {
  citation_id: string
  evidence_ref: string
  source_system: string
  source_type: string
  locator: string
  storage_mode: string
  content_hash?: string | null
  support_status: CanonicalEvidenceCitation['supportStatus']
  reason: string
}

interface RawLegacyAnswerPreviewData extends Omit<LegacyAnswerPreviewData, 'citations'> {
  citations?: RawEvidenceCitationData[]
}

export interface LegacyBacklogItemData extends LegacyBacklogItemContract {}

interface RawLegacyBacklogItemData extends Omit<LegacyBacklogItemData, 'citations'> {
  citations?: RawEvidenceCitationData[]
}

export interface LegacyAuditEventData {
  event_id: string
  actor: string
  action: string
  tenant_id: string
  project_id: string
  detail: string
  created_at: string
}

export interface LegacyUnderstandingOverviewData {
  project_id: string
  tenant_id: string
  status: 'ready' | 'needs-attention'
  generated_at: string
  summary: string
  sources: LegacySourceSummaryData[]
  format_support: LegacyFormatSupportData[]
  kpis: LegacyKpiData[]
  risks: LegacyRiskFindingData[]
  system_map: {
    nodes: Array<Record<string, unknown>>
    edges: Array<Record<string, unknown>>
  }
  system_map_report?: LegacySystemMapReportData | null
  answer_previews: LegacyAnswerPreviewData[]
  modernization_backlog: LegacyBacklogItemData[]
  audit_events: LegacyAuditEventData[]
}

interface RawLegacyUnderstandingOverviewData extends Omit<LegacyUnderstandingOverviewData, 'answer_previews' | 'modernization_backlog' | 'system_map_report'> {
  system_map_report?: RawLegacySystemMapReportData | null
  answer_previews: RawLegacyAnswerPreviewData[]
  modernization_backlog: RawLegacyBacklogItemData[]
}

export interface LegacyIntakeRequestData {
  tenant_id?: string | null
  scenario?: 'standard' | 'edge-cases'
  repositories?: Array<Record<string, unknown>>
  documents?: Array<Record<string, unknown>>
  connectors?: Array<Record<string, unknown>>
}

export interface LegacyPreflightCheckData {
  key: string
  label: string
  status: 'pass' | 'warn' | 'fail'
  detail: string
  remediation: string
  source_ids: string[]
}

export interface LegacyPreflightResponseData {
  project_id: string
  tenant_id: string
  status: 'ready' | 'needs-attention' | 'blocked'
  checks: LegacyPreflightCheckData[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

export interface LegacyQuestionResponseData {
  project_id: string
  tenant_id: string
  answer: LegacyAnswerPreviewData
}

interface RawLegacyQuestionResponseData extends Omit<LegacyQuestionResponseData, 'answer'> {
  answer: RawLegacyAnswerPreviewData
}

export interface LegacyJourneyStateUpdateData {
  active_audience: 'business' | 'developer' | 'admin'
  active_view: 'overview' | 'portfolio' | 'modules' | 'contracts' | 'docs' | 'hotspots' | 'backlog' | 'evidence' | 'trust'
  selected_evidence_ref?: string | null
  validation_states: Record<string, 'needs-review' | 'accepted' | 'rejected'>
  created_work_items: Record<string, string>
  generated_reports: Record<string, unknown>
  question: string
  question_answer?: LegacyAnswerPreviewData | null
}

export interface LegacyJourneyStateData extends LegacyJourneyStateUpdateData {
  project_id: string
  tenant_id: string
  updated_at: string
  updated_by: string
  audit_events: LegacyAuditEventData[]
}

interface RawLegacyJourneyStateData extends Omit<LegacyJourneyStateData, 'question_answer'> {
  question_answer?: RawLegacyAnswerPreviewData | null
}

export interface LegacyOperationalCheckData {
  key: string
  label: string
  category: 'journey-state' | 'report-generation' | 'evidence-hydration' | 'policy'
  status: 'pass' | 'warn' | 'fail'
  value: number
  unit: string
  target: number
  sample_count: number
  last_event_at?: string | null
  detail: string
}

export interface LegacyOperationalSnapshotData {
  project_id: string
  tenant_id: string
  generated_at: string
  overall_status: 'pass' | 'warn' | 'fail'
  checks: LegacyOperationalCheckData[]
  kpis: LegacyKpiData[]
  audit_events: LegacyAuditEventData[]
}

export interface LegacyRecoveryItemData {
  item_id: string
  kind: 'failed-report' | 'stale-workspace'
  severity: 'info' | 'medium' | 'high' | 'critical'
  status: 'open' | 'acknowledged' | 'resolved'
  title: string
  detail: string
  source: string
  detected_at: string
  owner: string
  recommended_action: string
  audit_refs: string[]
}

export interface LegacyRecoverySnapshotData {
  project_id: string
  tenant_id: string
  generated_at: string
  overall_status: 'ready' | 'needs-attention' | 'blocked'
  stale_after_hours: number
  items: LegacyRecoveryItemData[]
  audit_events: LegacyAuditEventData[]
}

export interface LegacyEvidenceClaimData extends LegacyEvidenceClaimContract {}

interface RawLegacyEvidenceClaimData extends Omit<LegacyEvidenceClaimData, 'citations'> {
  citations?: RawEvidenceCitationData[]
}

export interface LegacyEvidencePackData {
  schema_version: string
  pack_id: string
  project_id: string
  tenant_id: string
  audience: string
  generated_at: string
  raw_content_included: boolean
  storage_mode: 'reference-only' | 'customer-owned'
  sensitivity: 'internal' | 'restricted'
  summary: string
  claims: LegacyEvidenceClaimData[]
  kpi_snapshot: LegacyKpiData[]
  risk_snapshot: LegacyRiskFindingData[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

interface RawLegacyEvidencePackData extends Omit<LegacyEvidencePackData, 'claims'> {
  claims: RawLegacyEvidenceClaimData[]
}

export interface LegacyRepositoryManifestInputData {
  repo_id: string
  name: string
  source_url?: string | null
  default_branch?: string
  languages?: string[]
  estimated_files?: number
  estimated_pack_bytes?: number
  lfs_objects?: number
  unsupported_files?: number
  owner?: string | null
  sensitivity?: 'internal' | 'restricted' | 'regulated'
}

export interface LegacyRepositoryImportPlanData {
  repo_id: string
  name: string
  source_url: string | null
  default_branch: string
  languages: string[]
  owner: string | null
  sensitivity: string
  estimated_files: number
  estimated_pack_bytes: number
  lfs_objects: number
  unsupported_files: number
  policy_status: 'pass' | 'warn' | 'fail'
  preflight_status: 'ready' | 'needs-attention' | 'blocked'
  import_status: 'queued' | 'skipped' | 'blocked'
  storage_mode: 'reference-only' | 'customer-owned'
  findings: string[]
  next_actions: string[]
}

export interface LegacyBulkImportManifestData {
  schema_version: string
  manifest_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention' | 'blocked'
  repository_count: number
  queued_count: number
  blocked_count: number
  skipped_count: number
  total_estimated_files: number
  total_estimated_pack_bytes: number
  repositories: LegacyRepositoryImportPlanData[]
  audit_event: LegacyAuditEventData
}

export interface LegacyCodeQualityEngineMetricData {
  engine: 'cocoindex' | 'multilspy'
  language: 'java' | 'cpp'
  role: string
  status: 'pass' | 'warn' | 'fail'
  files_expected: number
  files_analyzed: number
  symbols_found: number
  relations_found: number
  confidence: number
  gaps: string[]
}

export interface LegacyCodeQualityLanguageMetricData {
  language: 'Java' | 'C++'
  fixture: string
  status: 'pass' | 'warn' | 'fail'
  fixture_files: number
  structural_coverage: number
  semantic_coverage: number
  reconciliation_score: number
  graph_quality_score: number
  engines: LegacyCodeQualityEngineMetricData[]
  findings: string[]
  next_actions: string[]
}

export type CodeIntelligenceEngineName = 'cocoindex' | 'multilspy' | 'xml-parser' | 'config-parser'

export interface CodeIntelligenceEngineData {
  engine: CodeIntelligenceEngineName
  role: string
  status: 'ready' | 'partial' | 'unavailable'
  languages: string[]
  confidence: number
  produced_facts: number
  produced_relationships: number
}

export type CodeIntelligenceOntologyNodeType = 'Repository' | 'Branch' | 'File' | 'Module' | 'Class' | 'Function' | 'Symbol' | 'IntegrationContract' | 'ConfigurationArtifact' | 'BuildSystem'
export type CodeIntelligenceOntologyEdgeType = 'CONTAINS' | 'DEFINES' | 'IMPLEMENTS' | 'CALLS' | 'REFERENCES' | 'CONFIGURED_BY' | 'DEPENDS_ON' | 'GENERATED_FROM' | 'VALIDATED_BY'

export interface CodeIntelligenceOntologyNodeProjectionData {
  node_type: CodeIntelligenceOntologyNodeType
  stable_id: string
  layer: 'source' | 'human' | 'agent' | 'memory'
  properties: Record<string, string | number | boolean>
}

export interface CodeIntelligenceOntologyEdgeProjectionData {
  edge_type: CodeIntelligenceOntologyEdgeType
  source_fact_id: string
  target_fact_id: string
  direction: 'as_observed' | 'inverted_from_relationship'
  layer: 'source' | 'human' | 'agent' | 'memory'
  properties: Record<string, string | number | boolean>
}

export interface CodeIntelligenceGraphSchemaData {
  schema_version: string
  ontology_packs: string[]
  node_types: CodeIntelligenceOntologyNodeType[]
  edge_types: CodeIntelligenceOntologyEdgeType[]
  filetype_node_type_map: Record<string, CodeIntelligenceOntologyNodeType>
  language_symbol_type_map: Record<string, CodeIntelligenceOntologyNodeType>
  relationship_type_map: Record<string, CodeIntelligenceOntologyEdgeType>
  inverted_relationship_types: string[]
  normalization_policy: string
}

export interface CodeIntelligenceFactData {
  fact_id: string
  fact_type: 'repository' | 'branch' | 'file' | 'module' | 'package' | 'class' | 'function' | 'method' | 'symbol' | 'dependency' | 'interface' | 'config_reference' | 'build_system' | 'generated_artifact'
  name: string
  path?: string | null
  language?: string | null
  engine: CodeIntelligenceEngineName
  confidence: number
  evidence_ref: string
  attributes: Record<string, string | number | boolean>
  ontology: CodeIntelligenceOntologyNodeProjectionData | null
}

export interface CodeIntelligenceRelationshipData {
  relationship_id: string
  source_fact_id: string
  target_fact_id: string
  relationship_type: 'contains' | 'declares' | 'implements' | 'calls' | 'references' | 'configures' | 'depends_on' | 'generated_from' | 'validated_by'
  engine: CodeIntelligenceEngineName
  confidence: number
  evidence_refs: string[]
  ontology: CodeIntelligenceOntologyEdgeProjectionData | null
}

export interface CodeIntelligenceModelData {
  schema_version: string
  corpus_id: string
  tenant_id: string
  project_id: string
  scenario: 'standard' | 'edge-cases'
  repositories: number
  branches: number
  files: number
  modules: number
  symbols: number
  dependencies: number
  interfaces: number
  config_references: number
  build_systems: number
  generated_artifacts: number
  confidence: number
  engine_reconciliation: number
  engines: CodeIntelligenceEngineData[]
  facts_preview: CodeIntelligenceFactData[]
  relationships_preview: CodeIntelligenceRelationshipData[]
  graph_schema: CodeIntelligenceGraphSchemaData | null
  governance: Record<string, string | number | boolean>
}

export interface LegacyCodeQualityReportData {
  schema_version: string
  report_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention'
  fixture_corpus_version: string
  intelligence_model: CodeIntelligenceModelData
  languages: LegacyCodeQualityLanguageMetricData[]
  kpis: LegacyKpiData[]
  audit_event: LegacyAuditEventData
}

export interface LegacyXmlBindingData {
  binding_id: string
  xml_path: string
  element: string
  selector: string
  target_type: 'java-class' | 'cpp-symbol' | 'bean' | 'resource' | 'unknown'
  target: string
  relation_type: 'configures' | 'references' | 'imports' | 'stale'
  confidence: number
  status: 'linked' | 'warn' | 'unresolved'
  evidence_refs: string[]
}

export interface LegacyXmlLinkReportData {
  schema_version: string
  report_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention'
  fixture_corpus_version: string
  files_analyzed: number
  intelligence_model: CodeIntelligenceModelData
  bindings: LegacyXmlBindingData[]
  kpis: LegacyKpiData[]
  findings: string[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

export interface LegacyConfigBindingData {
  binding_id: string
  config_path: string
  format: 'yaml' | 'json' | 'properties' | 'ini' | 'toml' | 'conf'
  key: string
  target_type: 'java-class' | 'cpp-symbol' | 'endpoint' | 'resource' | 'setting' | 'sensitive' | 'unknown'
  target: string
  relation_type: 'configures' | 'references' | 'sets' | 'sensitive' | 'stale'
  confidence: number
  status: 'linked' | 'warn' | 'unresolved'
  evidence_refs: string[]
}

export interface LegacyConfigLinkReportData {
  schema_version: string
  report_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention'
  fixture_corpus_version: string
  files_analyzed: number
  intelligence_model: CodeIntelligenceModelData
  bindings: LegacyConfigBindingData[]
  kpis: LegacyKpiData[]
  findings: string[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

export interface LegacyPdfCitationSpanData {
  citation_id: string
  document_id: string
  title: string
  source_type: 'sharepoint' | 'file-store' | 'upload'
  page_start: number
  page_end: number
  char_start: number
  char_end: number
  claim: string
  evidence_ref: string
  confidence: number
  status: 'citable' | 'warn' | 'blocked'
}

export interface LegacyDocumentAssessmentData {
  document_id: string
  title: string
  source_type: 'sharepoint' | 'file-store' | 'upload'
  format: 'pdf'
  status: 'current' | 'stale' | 'duplicate' | 'needs-ocr'
  pages: number
  text_pages: number
  scanned_pages: number
  modified_at: string
  duplicate_of: string | null
  stale_reason: string | null
  storage_mode: 'reference-only' | 'customer-owned'
  findings: string[]
}

export interface LegacyPdfCitationReportData {
  schema_version: string
  report_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention'
  fixture_corpus_version: string
  documents: LegacyDocumentAssessmentData[]
  citations: LegacyPdfCitationSpanData[]
  kpis: LegacyKpiData[]
  findings: string[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

export interface LegacySystemMapNodeData extends LegacySystemMapNodeContract {}

export interface LegacySystemMapEdgeData extends LegacySystemMapEdgeContract {}

interface RawLegacySystemMapNodeData extends Omit<LegacySystemMapNodeData, 'citations'> {
  citations?: RawEvidenceCitationData[]
}

interface RawLegacySystemMapEdgeData extends Omit<LegacySystemMapEdgeData, 'citations'> {
  citations?: RawEvidenceCitationData[]
}

export interface LegacySystemMapReportData {
  schema_version: string
  report_id: string
  project_id: string
  tenant_id: string
  generated_at: string
  status: 'ready' | 'needs-attention'
  ontology_pack_id: string
  serialization_profile: string
  active_node_types: string[]
  nodes: LegacySystemMapNodeData[]
  edges: LegacySystemMapEdgeData[]
  kpis: LegacyKpiData[]
  findings: string[]
  next_actions: string[]
  audit_event: LegacyAuditEventData
}

interface RawLegacySystemMapReportData extends Omit<LegacySystemMapReportData, 'nodes' | 'edges'> {
  nodes: RawLegacySystemMapNodeData[]
  edges: RawLegacySystemMapEdgeData[]
}

export interface ConversationData {
  conversation_id: string
  project_id: string
  tenant_id: string
  owner_user_id: string
  title: string
  persona_id: string
  created_at: string
  updated_at: string
}

export interface MessageData {
  message_id: string
  conversation_id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface ConnectionPeer {
  node_id: string
  title: string
  node_type: string
  status: string
}

export interface ConnectionEntry {
  edge: KnowledgeEdgeData
  direction: 'incoming' | 'outgoing'
  peer: ConnectionPeer
}

export interface ConnectionsResult {
  incoming: ConnectionEntry[]
  outgoing: ConnectionEntry[]
}

export interface SpecTemplateData {
  template_id: string
  name: string
  description: string
  sections: { number: string; title: string; description: string; default_content: string }[]
}

export interface SpecInstanceData {
  spec_id: string
  project_id: string
  template_id: string
  title: string
  created_at: string
}

export interface WorkItemDraft {
  level: string
  title: string
  description: string
  parent_id?: string | null
  owner_persona: string
  source_node_id?: string
  source_type?: string
  tags: string[]
}

export interface RelatedItemsResult {
  related_assets: { node_id: string; asset_id: string; link_type: string }[]
  related_annotations: KnowledgeNodeData[]
  total_assets: number
  total_annotations: number
}

export interface WorkItemData {
  item_id: string
  project_id: string
  tenant_id: string
  owner_user_id: string
  level: 'epic' | 'story' | 'implementation'
  title: string
  description: string
  parent_id: string | null
  owner_persona: string
  status: string
  linked_node_ids: string[]
  tags: string[]
  children?: WorkItemData[]
  progress?: number
  created_at: string
  updated_at: string
}

export interface LLMGatewayProfile {
  gateway_id: string
  name: string
  provider: string
  model: string
  max_tokens: number
  temperature: number
  api_key_configured: boolean
  api_base_url: string
  description: string
}

export interface LLMGatewayUpsert {
  gateway_id: string
  name: string
  provider: string
  model: string
  max_tokens?: number
  temperature?: number
  api_key?: string
  api_base_url?: string
  description?: string
}

export interface AnnotationCommentData {
  comment_id: string
  node_id: string
  project_id: string
  tenant_id: string
  author_user_id: string
  author_persona: string
  content: string
  parent_comment_id: string | null
  created_at: string
}

export interface ActivityEventData {
  event_id: string
  project_id: string
  tenant_id: string
  actor_user_id: string
  actor_persona: string
  event_type: string
  target_node_id: string | null
  summary: string
  metadata: Record<string, unknown>
  created_at: string
}

export interface HandoffSummaryItem {
  id: string
  title: string
  content: string
  meta?: Record<string, unknown>
}

export interface HandoffSummarySection {
  title: string
  icon: string
  items: HandoffSummaryItem[]
}

export interface HandoffSummary {
  from_persona: string
  to_persona: string
  sections: HandoffSummarySection[]
  node_count: number
}

export interface SpecSnapshotData {
  snapshot_id: string
  spec_id: string
  project_id: string
  version: number
  label: string
  section_count: number
  sections: { number: string; title: string; content: string }[]
  created_at: string
}

export interface SpecSnapshotSummary {
  snapshot_id: string
  spec_id: string
  version: number
  label: string
  section_count: number
  created_at: string
}

export interface SpecDiffEntry {
  number: string
  title: string
  status: 'unchanged' | 'modified' | 'added' | 'removed'
  old_content?: string
  new_content?: string
}

export interface SpecListItem {
  spec_id: string
  template_id: string | null
  section_count: number
  sections: { node_id: string; number: string; title: string; updated_at: string | null }[]
}

export interface DataAssetData {
  asset_id: string
  source_type: string
  project_id: string
  tenant_id: string
  owner_user_id: string
  filename: string
  mime_type: string | null
  size_bytes: number
  storage_path: string
  session_id: string | null
  visibility: string
  shared_with_id: string | null
  parse_strategy: string
  index_status: 'none' | 'pending' | 'indexing' | 'indexed' | 'failed'
  chunk_count: number
  source_config_id: string | null
  external_id: string | null
  external_url: string | null
  metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface AssetChunkData {
  chunk_id: string
  asset_id: string
  chunk_index: number
  content_text: string
  metadata: Record<string, unknown>
  linked_knowledge_node_id: string | null
  created_at: string
}

export interface DataSourceConfigData {
  config_id: string
  source_type: string
  name: string
  tenant_id: string
  auth_mode: string
  auth_config: Record<string, unknown>
  sync_config: Record<string, unknown>
  enabled: boolean
  created_at: string
  updated_at: string
}

export interface SyncStateData {
  config_id: string
  sync_state?: Record<string, unknown>
  last_sync_at: string | null
  last_sync_status: string
  last_error: string | null
  items_synced: number
  created_at?: string
  updated_at?: string
}

export interface QuotaConfigData {
  defaults: {
    max_upload_size_bytes: number
    max_total_storage_bytes: number
    max_assets_per_project: number
    allowed_mime_types: string[]
  }
  scopes: Record<string, {
    max_upload_size_bytes?: number
    max_total_storage_bytes?: number
    max_assets_per_project?: number
    allowed_mime_types?: string[]
  }>
}

export interface UsageSummaryData {
  total_bytes: number
  total_assets: number
  total_configs: number
  total_users: number
}

export interface UsageBreakdownItem {
  scope_type: string
  scope_id: string
  total_bytes: number
  asset_count: number
}

export interface TopUserUsage {
  owner_user_id: string
  asset_count: number
  total_bytes: number
}

export interface SchedulerStatusData {
  running: boolean
  scheduled_configs: {
    config_id: string
    name: string
    source_type: string
    interval_minutes: number
    last_sync_at: string | null
    last_status: string
  }[]
}

export interface UnifiedSearchResultItem {
  type: 'knowledge' | 'datasource'
  id: string
  title: string
  content: string
  score: number
  metadata: Record<string, unknown>
}

export interface UnifiedSearchResult {
  results: UnifiedSearchResultItem[]
  total: number
}

export interface GitTreeEntry {
  name: string
  path: string
  type: 'blob' | 'tree'
  size?: number
  mode?: string
}

export interface RuntimeCapabilityDescriptor {
  capability_id: string
  role: string
  environment: string
  toolset: string[]
  trust_tier: string
  device_class: string
  repo_access?: string[]
  network_mode?: string
  health?: string
  specialist_endpoint?: string | null
}

export interface RuntimeRouteResponse {
  selected: RuntimeCapabilityDescriptor | null
  selected_reasoning?: string[]
  candidates: Array<Record<string, unknown>>
  task_type: string
  preferred_execution_target: string
  repo_id: string
  route_source: string
  graph_hints: Record<string, unknown>
  handoff_summary?: Record<string, unknown>
}

export interface GraphIntelligenceGateStatus {
  kpi_id: string
  metric: string
  score: number
  threshold: number
  direction: 'higher_is_better' | 'lower_is_better'
  passed: boolean
  blocking: boolean
}

export interface GraphIntelligenceRolloutFlags {
  mode: 'off' | 'shadow' | 'assistive'
  route_priors_enabled: boolean
  assistive_enabled: boolean
  temporal_rules_in_route_priors: boolean
  source: string
}

export interface GraphIntelligenceRolloutStatus {
  tenant_id: string
  setting_key: string
  configured: Record<string, unknown>
  effective: GraphIntelligenceRolloutFlags
  gates: GraphIntelligenceGateStatus[]
  gates_passed: boolean
  blocking_reasons: string[]
}

export interface GraphIntelligenceRolloutUpdate {
  mode: 'off' | 'shadow' | 'assistive'
  route_priors_enabled?: boolean
  assistive_enabled?: boolean
  temporal_rules_in_route_priors?: boolean
  require_gates_passed?: boolean
}

export interface RuntimeContextSlice {
  slice_id: string
  objective: string
  entity_ids?: string[]
  fact_ids?: string[]
  knowledge_node_ids?: string[]
  file_paths?: string[]
  graph_paths?: string[]
  summary?: string
  token_estimate?: number
  freshness_ms?: number
  confidence_score?: number
  provenance?: Record<string, unknown>
}

export interface RuntimeSubtaskRecord {
  subtask_id: string
  status: string
  capability_id?: string | null
  request_id?: string | null
  session_id?: string | null
  contract: Record<string, unknown>
  context?: Record<string, unknown>
  verdict?: Record<string, unknown> | null
  created_at?: string
  updated_at?: string
}

export interface RuntimeHandoffRecord {
  handoff_id: string
  status?: string
  subtask_id?: string
  recipient_capability_id: string
  context_slice_refs?: string[]
  artifact_refs?: string[]
  policy_version?: string
  created_at?: string
  expires_at?: string
  artifact_records?: Array<Record<string, unknown>>
}

export interface RuntimeReplayPayload {
  subtask: RuntimeSubtaskRecord
  events: Array<Record<string, unknown>>
  handoffs: RuntimeHandoffRecord[]
  artifact_refs: Record<string, unknown>
}

export interface AIModelCatalogEntry {
  alias: string
  name: string
  provider: string
  upstream_model: string
  description: string
  enabled: boolean
  supports_tools: boolean
  supports_vision: boolean
  max_input_tokens: number
  max_output_tokens: number
  temperature: number
  sensitivity: 'standard' | 'restricted' | 'local'
  tags: string[]
}

export interface AIModelCatalogDocument {
  models: AIModelCatalogEntry[]
}

export interface AIModelEntitlements {
  default_alias: string | null
  allowed_aliases: string[]
  workload_aliases: Record<string, string>
  role_aliases: Record<string, string[]>
  max_input_tokens: number | null
  max_output_tokens: number | null
  daily_request_budget: number | null
  daily_token_budget: number | null
  user_daily_request_budget: number | null
  user_daily_token_budget: number | null
}

export interface AIUsageCounter {
  requests: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
}

export interface AIUsageModelRecord extends AIUsageCounter {
  provider: string
  upstream_model: string
}

export interface AIUsageSummary {
  totals: AIUsageCounter
  by_model: Record<string, AIUsageModelRecord>
  by_user: Record<string, AIUsageCounter>
  by_workload: Record<string, AIUsageCounter>
  days: Array<{
    date: string
    by_model: Record<string, AIUsageModelRecord>
    by_user: Record<string, AIUsageCounter>
    by_workload: Record<string, AIUsageCounter>
    totals: AIUsageCounter
  }>
}

export interface WorkspacePullRequest {
  pr_id: string
  repo_id: string
  tenant_id: string
  title: string
  description: string
  source_branch: string
  target_branch: string
  status: string
  created_by: string
  created_at: string
  updated_at: string
  merged_at?: string | null
  merge_commit?: string | null
}

export interface WorkspaceSshKey {
  key_id: string
  title: string
  public_key: string
  fingerprint: string
  created_at: string
  created_by: string
}

export interface WorkspaceRepoSummary {
  repo_id: string
  name: string
  git_repo_name: string
  canonical_path: string
  tenant_id: string
  description: string
  visibility: string
  default_branch: string
  http_clone_url: string
  ssh_clone_url: string
  branch_count: number
  commit_count: number
  forked_from?: string | null
  created_at: string
  updated_at: string
}

export interface WorkspaceRepoDetail extends WorkspaceRepoSummary {
  branches: string[]
  pull_requests: WorkspacePullRequest[]
}

export interface WorkspaceFileMutationResult {
  ok: boolean
  path: string
  branch: string
  commit: string
}

export interface UxEventInput {
  event_name: string
  persona_id?: string
  action_id?: string
  session_id?: string
  metadata?: Record<string, unknown>
  occurred_at?: string
}

export interface Session {
  session_id: string
  message_count: number
  created_at: string
}

export interface Tool {
  name: string
  description: string
  parameters: Record<string, unknown>
}

class ApiService {
  private token: string | null = null
  private authEpoch = 0
  private chatRequests = new Set<AbortController>()
  private ws: WebSocket | null = null
  private wsReady: Promise<void> | null = null
  private pending = new Map<
    string,
    {
      resolve: (response: ChatResponse) => void
      reject: (error: Error) => void
    }
  >()
  private updateListeners = new Set<(event: AgentProgressEvent) => void>()
  private confirmationListeners = new Set<(frame: AgentConfirmationRequiredFrame) => void>()
  private pendingUpdates = new Map<
    string,
    {
      resolve: (events: AgentProgressEvent[]) => void
      reject: (error: Error) => void
    }
  >()

  private makeId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
    return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }

  private normalizeToolCalls(toolCalls: unknown): ToolCall[] {
    if (!Array.isArray(toolCalls)) {
      return []
    }
    return toolCalls
      .map((tc: any) => {
        if (tc?.function?.name) {
          return {
            id: String(tc.id || this.makeId()),
            function: {
              name: String(tc.function.name),
              arguments: typeof tc.function.arguments === 'string' ? tc.function.arguments : JSON.stringify(tc.function.arguments || {})
            }
          } as ToolCall
        }
        if (tc?.name) {
          return {
            id: String(tc.id || this.makeId()),
            function: {
              name: String(tc.name),
              arguments: typeof tc.arguments === 'string' ? tc.arguments : JSON.stringify(tc.arguments || {})
            }
          } as ToolCall
        }
        return null
      })
      .filter((tc): tc is ToolCall => tc !== null)
  }

  private normalizeChatResponse(response: ChatResponse): ChatResponse {
    return {
      ...response,
      tool_calls: this.normalizeToolCalls(response.tool_calls),
      citations: normalizeEvidenceCitations(response.citations),
    }
  }

  setToken(token: string | null) {
    if (token !== this.token || token === null) {
      this.authEpoch += 1
      const socket = this.ws
      this.ws = null
      this.wsReady = null
      const error = new Error('Account changed; start a new conversation')
      for (const pending of this.pending.values()) pending.reject(error)
      this.pending.clear()
      for (const pending of this.pendingUpdates.values()) pending.reject(error)
      this.pendingUpdates.clear()
      for (const controller of this.chatRequests) controller.abort()
      this.chatRequests.clear()
      socket?.close()
    }
    this.token = token
  }

  onProgress(handler: (event: AgentProgressEvent) => void): () => void {
    this.updateListeners.add(handler)
    return () => this.updateListeners.delete(handler)
  }

  onConfirmationRequired(handler: (frame: AgentConfirmationRequiredFrame) => void): () => void {
    this.confirmationListeners.add(handler)
    return () => this.confirmationListeners.delete(handler)
  }

  async sendConfirmationResponse(payload: ConfirmationResponsePayload): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload))
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {})
    }

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    return response.json()
  }

  previewOKF(projectId: string, upload: OKFUpload) {
    return this.request<OKFPreview>(`/knowledge/okf/preview?project_id=${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(upload) })
  }

  publishOKF(projectId: string, upload: OKFUpload) {
    return this.request<OKFRecord>(`/knowledge/okf/revisions?project_id=${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(upload) })
  }

  listOKF(projectId: string, kind: 'source' | 'note', offset = 0) {
    return this.request<{ records: OKFRecord[] }>(`/knowledge/okf/records?project_id=${encodeURIComponent(projectId)}&kind=${kind}&offset=${offset}`)
  }

  readOKF(projectId: string, revisionId: string) {
    return this.request<OKFRevision>(`/knowledge/okf/revisions/${encodeURIComponent(revisionId)}?project_id=${encodeURIComponent(projectId)}`)
  }

  readOKFNote(projectId: string, noteId: string) {
    return this.request<OKFNote & { author_id: string }>(`/knowledge/okf/notes/${encodeURIComponent(noteId)}?project_id=${encodeURIComponent(projectId)}`)
  }

  addOKFNote(projectId: string, note: OKFNote) {
    return this.request<OKFRecord>(`/knowledge/okf/notes?project_id=${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(note) })
  }

  withdrawOKF(projectId: string, artifactId: string) {
    return this.request<{ status: string }>(`/knowledge/okf/records/${encodeURIComponent(artifactId)}/withdraw?project_id=${encodeURIComponent(projectId)}`, { method: 'POST' })
  }

  resolveOKF(projectId: string, request: OKFContextRequest) {
    return this.request<OKFContext>(`/knowledge/okf/context?project_id=${encodeURIComponent(projectId)}`, { method: 'POST', body: JSON.stringify(request) })
  }

  // Agent
  async chat(
    message: string,
    sessionId?: string,
    intentContext?: ChatIntentContext,
    tier?: string | null,
    projectId?: string,
    persona?: string,
  ): Promise<ChatResponse> {
    const epoch = this.authEpoch
    try {
      const response = await this.chatViaWebSocket(message, sessionId, intentContext, tier, projectId, persona)
      if (epoch !== this.authEpoch) throw new Error('Account changed; start a new conversation')
      return response
    } catch (err) {
      if (epoch !== this.authEpoch) throw new Error('Account changed; start a new conversation')
      const controller = new AbortController()
      this.chatRequests.add(controller)
      try {
        const response = await this.request<ChatResponse>('/agent/chat', {
          method: 'POST',
          signal: controller.signal,
          body: JSON.stringify({
            message,
            session_id: sessionId,
            context: {
              ...(intentContext ? { intent_context: intentContext } : {}),
              ...(projectId ? { project_id: projectId } : {}),
              ...(persona ? { persona } : {}),
            },
            ...(tier ? { tier } : {}),
          }),
        })
        if (epoch !== this.authEpoch) throw new Error('Account changed; start a new conversation')
        return this.normalizeChatResponse(response)
      } finally {
        this.chatRequests.delete(controller)
      }
    }
  }

  async getSessions(): Promise<Session[]> {
    return this.request<Session[]>('/agent/sessions')
  }

  async getTools(): Promise<Tool[]> {
    return this.request<Tool[]>('/agent/tools')
  }

  // Health
  async health(): Promise<{ status: string }> {
    return this.request<{ status: string }>('/health')
  }

  // Knowledge Graph
  async listKnowledgeNodes(
    projectId: string,
    status?: string,
    nodeType?: string,
    limit = 100,
    branchRef?: string,
  ): Promise<{ nodes: KnowledgeNodeData[] }> {
    return this.request<{ nodes: KnowledgeNodeData[] }>('/knowledge/nodes', {
      method: 'POST',
      body: JSON.stringify({
        project_id: projectId,
        status,
        node_type: nodeType,
        limit,
        ...(branchRef != null ? { branch_ref: branchRef } : {}),
      }),
    })
  }

  async getKnowledgeNode(nodeId: string): Promise<KnowledgeNodeData> {
    return this.request<KnowledgeNodeData>(`/knowledge/nodes/${nodeId}`)
  }

  async promoteKnowledgeNode(nodeId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/knowledge/nodes/${nodeId}/promote`, {
      method: 'POST',
    })
  }

  async dismissKnowledgeNode(nodeId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/knowledge/nodes/${nodeId}/dismiss`, {
      method: 'POST',
    })
  }

  async updateKnowledgeNode(
    nodeId: string,
    updates: Partial<{ title: string; content: string; tags: string[] }>,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/knowledge/nodes/${nodeId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  async getKnowledgeStats(
    projectId: string,
  ): Promise<{ project_id: string; counts: Record<string, number> }> {
    return this.request<{ project_id: string; counts: Record<string, number> }>(
      `/knowledge/projects/${projectId}/stats`,
    )
  }

  async setKnowledgeVisibility(
    nodeId: string,
    visibility: 'personal' | 'project' | 'team' | 'organization',
  ): Promise<{ ok: boolean; visibility: string }> {
    return this.request<{ ok: boolean; visibility: string }>(
      `/knowledge/nodes/${nodeId}/visibility`,
      { method: 'POST', body: JSON.stringify({ visibility }) },
    )
  }

  async listSharedKnowledgeNodes(
    projectId: string,
    status?: string,
    nodeType?: string,
    branchRef?: string,
  ): Promise<{ nodes: KnowledgeNodeData[] }> {
    return this.request<{ nodes: KnowledgeNodeData[] }>(
      `/knowledge/projects/${projectId}/shared`,
      {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          status,
          node_type: nodeType,
          ...(branchRef != null ? { branch_ref: branchRef } : {}),
        }),
      },
    )
  }

  async organizeKnowledge(projectId: string): Promise<{ status: string }> {
    return this.request<{ status: string }>(
      `/knowledge/projects/${projectId}/organize`,
      { method: 'POST' },
    )
  }

  async deleteKnowledgeNode(nodeId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      `/knowledge/nodes/${nodeId}`,
      { method: 'DELETE' },
    )
  }

  async linkKnowledgeNodes(
    projectId: string,
    sourceNodeId: string,
    targetNodeId: string,
    edgeType: string = 'relates_to',
    weight: number = 0.8,
    metadata?: Record<string, unknown>,
  ): Promise<{ ok: boolean; edge: KnowledgeEdgeData }> {
    return this.request<{ ok: boolean; edge: KnowledgeEdgeData }>(
      `/knowledge/projects/${projectId}/link`,
      {
        method: 'POST',
        body: JSON.stringify({
          source_node_id: sourceNodeId,
          target_node_id: targetNodeId,
          edge_type: edgeType,
          weight,
          metadata,
        }),
      },
    )
  }

  async listKnowledgeEdges(projectId: string): Promise<{ edges: KnowledgeEdgeData[] }> {
    return this.request<{ edges: KnowledgeEdgeData[] }>(
      `/knowledge/projects/${projectId}/edges`,
    )
  }

  async getConnections(nodeId: string, projectId: string): Promise<ConnectionsResult> {
    return this.request<ConnectionsResult>(
      `/knowledge/nodes/${nodeId}/connections?project_id=${encodeURIComponent(projectId)}`,
    )
  }

  async pinKnowledge(params: {
    selected_text: string
    project_id: string
    node_type?: string
    title?: string
    conversation_id?: string
    message_id?: string
    session_id?: string
  }): Promise<{ ok: boolean; node: KnowledgeNodeData }> {
    return this.request<{ ok: boolean; node: KnowledgeNodeData }>(
      '/knowledge/pin',
      { method: 'POST', body: JSON.stringify(params) },
    )
  }

  async saveTopic(params: {
    project_id: string
    title: string
    query: string
    file_refs: ContextBundleFileRef[]
    knowledge_refs: string[]
    external_refs: ContextBundleExternalRef[]
    conversation_id?: string
    session_id?: string
  }): Promise<{ ok: boolean; topic: KnowledgeNodeData; edges_created: number }> {
    return this.request('/knowledge/topics/save', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  async shareKnowledgeNode(
    nodeId: string,
    visibility: 'personal' | 'project' | 'team' | 'organization',
    sharedWithId?: string,
    sharedWithName?: string,
  ): Promise<{ ok: boolean; visibility: string; shared_with_id: string | null }> {
    return this.request(`/knowledge/nodes/${nodeId}/share`, {
      method: 'POST',
      body: JSON.stringify({
        visibility,
        shared_with_id: sharedWithId || null,
        shared_with_name: sharedWithName || null,
      }),
    })
  }

  async unshareKnowledgeNode(nodeId: string): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(`/knowledge/nodes/${nodeId}/unshare`, {
      method: 'POST',
    })
  }

  async getShareHistory(nodeId: string): Promise<{ events: KnowledgeShareEventData[] }> {
    return this.request<{ events: KnowledgeShareEventData[] }>(
      `/knowledge/nodes/${nodeId}/share-history`,
    )
  }

  async getMyMemberships(): Promise<{
    teams: { id: string; name: string; org_id?: string }[]
    organizations: { id: string; name: string }[]
  }> {
    return this.request('/auth/me/memberships')
  }

  // Data Sources
  async uploadFile(
    projectId: string,
    file: File,
    parseStrategy = 'chunk',
    storageMode = 'reference-only',
    sessionId?: string,
  ): Promise<{ ok: boolean; asset: DataAssetData }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('project_id', projectId)
    formData.append('parse_strategy', parseStrategy)
    formData.append('storage_mode', storageMode)
    if (sessionId) formData.append('session_id', sessionId)

    const headers: Record<string, string> = {}
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`

    const response = await fetch(`${API_BASE}/datasources/upload`, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }))
      throw new Error(error.detail || `HTTP ${response.status}`)
    }

    return response.json()
  }

  async listDataAssets(
    projectId: string,
    status?: string,
    limit = 100,
  ): Promise<{ assets: DataAssetData[] }> {
    return this.request('/datasources/assets', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, status, limit }),
    })
  }

  async getDataAsset(assetId: string): Promise<{ asset: DataAssetData }> {
    return this.request(`/datasources/assets/${assetId}`)
  }

  async deleteDataAsset(assetId: string): Promise<{ ok: boolean }> {
    return this.request(`/datasources/assets/${assetId}`, { method: 'DELETE' })
  }

  async shareDataAsset(
    assetId: string,
    visibility: string,
    sharedWithId?: string,
  ): Promise<{ ok: boolean }> {
    return this.request(`/datasources/assets/${assetId}/share`, {
      method: 'POST',
      body: JSON.stringify({ visibility, shared_with_id: sharedWithId || null }),
    })
  }

  async unshareDataAsset(assetId: string): Promise<{ ok: boolean }> {
    return this.request(`/datasources/assets/${assetId}/unshare`, { method: 'POST' })
  }

  async listAssetChunks(assetId: string): Promise<{ chunks: AssetChunkData[] }> {
    return this.request(`/datasources/assets/${assetId}/chunks`)
  }

  async linkChunkToKnowledge(
    assetId: string,
    chunkId: string,
    knowledgeNodeId: string,
  ): Promise<{ ok: boolean }> {
    return this.request(`/datasources/assets/${assetId}/chunks/${chunkId}/link`, {
      method: 'POST',
      body: JSON.stringify({ knowledge_node_id: knowledgeNodeId }),
    })
  }

  async reparseAsset(
    assetId: string,
    parseStrategy?: string,
  ): Promise<{ ok: boolean; chunk_count: number }> {
    return this.request(`/datasources/assets/${assetId}/reparse`, {
      method: 'POST',
      body: JSON.stringify({ parse_strategy: parseStrategy || null }),
    })
  }

  async getStorageUsage(): Promise<{ total_bytes: number; asset_count: number }> {
    return this.request('/datasources/usage')
  }

  // ── Data Source Config Admin ──────────────────────────────────────

  async listDatasourceConfigs(): Promise<{ configs: DataSourceConfigData[] }> {
    return this.request('/datasources/configs')
  }

  async getDatasourceConfig(configId: string): Promise<{ config: DataSourceConfigData }> {
    return this.request(`/datasources/configs/${configId}`)
  }

  async createDatasourceConfig(data: {
    source_type: string
    name: string
    auth_mode?: string
    auth_config?: Record<string, unknown>
    sync_config?: Record<string, unknown>
    enabled?: boolean
  }): Promise<{ config: DataSourceConfigData }> {
    return this.request('/datasources/configs', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateDatasourceConfig(
    configId: string,
    data: {
      name?: string
      auth_mode?: string
      auth_config?: Record<string, unknown>
      sync_config?: Record<string, unknown>
      enabled?: boolean
    },
  ): Promise<{ ok: boolean }> {
    return this.request(`/datasources/configs/${configId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteDatasourceConfig(configId: string): Promise<{ ok: boolean }> {
    return this.request(`/datasources/configs/${configId}`, { method: 'DELETE' })
  }

  async testDatasourceConnection(configId: string): Promise<{ ok: boolean; message: string; info: Record<string, unknown> }> {
    return this.request(`/datasources/configs/${configId}/test`, { method: 'POST' })
  }

  async triggerDatasourceSync(
    configId: string,
    projectId: string,
    maxPages?: number,
  ): Promise<{ task_id: string; status: string }> {
    return this.request(`/datasources/configs/${configId}/sync`, {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId, max_pages: maxPages || 10 }),
    })
  }

  async getDatasourceSyncState(configId: string): Promise<SyncStateData> {
    return this.request(`/datasources/configs/${configId}/sync-state`)
  }

  // ── Demo Integrations ─────────────────────────────────────────────

  async getDemoIntegrationStatus(): Promise<{
    integrations: Record<string, {
      name: string
      source_type: string
      configured: boolean
      config_id: string | null
      enabled: boolean
    }>
  }> {
    return this.request('/datasources/demo/status')
  }

  async toggleDemoIntegration(params: {
    integration: string
    enabled: boolean
    project_id?: string
    auto_sync?: boolean
  }): Promise<{ ok: boolean; action: string; config_id?: string; sync_task_id?: string }> {
    return this.request('/datasources/demo/toggle', {
      method: 'POST',
      body: JSON.stringify(params),
    })
  }

  // ── Legacy Software Understanding ───────────────────────────────────────

  async getLegacyUnderstandingOverview(projectId: string): Promise<LegacyUnderstandingOverviewData> {
    return normalizeLegacyOverview(await this.request<RawLegacyUnderstandingOverviewData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/overview`))
  }

  async ingestLegacyUnderstandingSources(
    projectId: string,
    data: LegacyIntakeRequestData,
  ): Promise<LegacyUnderstandingOverviewData> {
    return normalizeLegacyOverview(await this.request<RawLegacyUnderstandingOverviewData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/intake`, {
      method: 'POST',
      body: JSON.stringify(data),
    }))
  }

  async preflightLegacyUnderstandingSources(
    projectId: string,
    data: LegacyIntakeRequestData,
  ): Promise<LegacyPreflightResponseData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/preflight`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async askLegacyUnderstandingQuestion(
    projectId: string,
    question: string,
  ): Promise<LegacyQuestionResponseData> {
    return normalizeLegacyQuestionResponse(await this.request<RawLegacyQuestionResponseData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/questions`, {
      method: 'POST',
      body: JSON.stringify({ question }),
    }))
  }

  async getLegacyJourneyState(projectId: string): Promise<LegacyJourneyStateData> {
    return normalizeLegacyJourneyState(await this.request<RawLegacyJourneyStateData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/journey-state`))
  }

  async updateLegacyJourneyState(
    projectId: string,
    data: LegacyJourneyStateUpdateData,
  ): Promise<LegacyJourneyStateData> {
    return normalizeLegacyJourneyState(await this.request<RawLegacyJourneyStateData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/journey-state`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }))
  }

  async getLegacyOperationalSnapshot(projectId: string): Promise<LegacyOperationalSnapshotData> {
    return this.request<LegacyOperationalSnapshotData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/operational-snapshot`)
  }

  async getLegacyRecoverySnapshot(projectId: string): Promise<LegacyRecoverySnapshotData> {
    return this.request<LegacyRecoverySnapshotData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/recovery-snapshot`)
  }

  async createLegacyEvidencePack(
    projectId: string,
    data: { audience: 'customer-workshop' | 'security-review' | 'technical-discovery'; include_raw_content?: boolean },
  ): Promise<LegacyEvidencePackData> {
    return normalizeLegacyEvidencePack(await this.request<RawLegacyEvidencePackData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/evidence-pack`, {
      method: 'POST',
      body: JSON.stringify(data),
    }))
  }

  async createLegacyBulkImportManifest(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases'; repositories?: LegacyRepositoryManifestInputData[] },
  ): Promise<LegacyBulkImportManifestData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/bulk-import-manifest`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createLegacyCodeQualityReport(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases' },
  ): Promise<LegacyCodeQualityReportData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/code-quality`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createLegacyXmlLinkReport(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases' },
  ): Promise<LegacyXmlLinkReportData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/xml-links`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createLegacyConfigLinkReport(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases' },
  ): Promise<LegacyConfigLinkReportData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/config-links`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createLegacyPdfCitationReport(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases' },
  ): Promise<LegacyPdfCitationReportData> {
    return this.request(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/pdf-citations`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async createLegacySystemMapReport(
    projectId: string,
    data: { scenario?: 'standard' | 'edge-cases' },
  ): Promise<LegacySystemMapReportData> {
    return normalizeLegacySystemMapReport(await this.request<RawLegacySystemMapReportData>(`/legacy-understanding/projects/${encodeURIComponent(projectId)}/system-map`, {
      method: 'POST',
      body: JSON.stringify(data),
    }))
  }

  // ── Admin: Quotas & Usage ──────────────────────────────────────────

  async getQuotaConfig(): Promise<QuotaConfigData> {
    return this.request('/admin/quotas')
  }

  async setQuotaConfig(data: {
    scope: string
    max_upload_size_bytes?: number
    max_total_storage_bytes?: number
    max_assets_per_project?: number
    allowed_mime_types?: string[]
  }): Promise<{ ok: boolean }> {
    return this.request('/admin/quotas', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteQuotaScope(scope: string): Promise<{ ok: boolean }> {
    return this.request(`/admin/quotas/${scope}`, { method: 'DELETE' })
  }

  async getUsageSummary(): Promise<UsageSummaryData> {
    return this.request('/admin/usage/summary')
  }

  async getUsageBreakdown(): Promise<{ usage: UsageBreakdownItem[] }> {
    return this.request('/admin/usage/breakdown')
  }

  async getTopUsersUsage(): Promise<{ users: TopUserUsage[] }> {
    return this.request('/admin/usage/top-users')
  }

  // ── Admin: Scheduler ──────────────────────────────────────────────

  async startScheduler(): Promise<{ ok: boolean; running: boolean }> {
    return this.request('/admin/scheduler/start', { method: 'POST' })
  }

  async stopScheduler(): Promise<{ ok: boolean; running: boolean }> {
    return this.request('/admin/scheduler/stop', { method: 'POST' })
  }

  async getSchedulerStatus(): Promise<SchedulerStatusData> {
    return this.request('/admin/scheduler/status')
  }

  // ── Admin: Tenant Settings ───────────────────────────────────────

  async getTenantSettings(tenantId: string): Promise<{ settings: { setting_key: string; setting_value: Record<string, unknown>; updated_at: string; updated_by: string }[] }> {
    return this.request(`/admin/settings/${encodeURIComponent(tenantId)}`)
  }

  async getTenantSetting(tenantId: string, key: string): Promise<{ setting_key: string; setting_value: Record<string, unknown> }> {
    return this.request(`/admin/settings/${encodeURIComponent(tenantId)}/${encodeURIComponent(key)}`)
  }

  async setTenantSetting(tenantId: string, key: string, value: Record<string, unknown>): Promise<{ ok: boolean; setting_key: string }> {
    return this.request(`/admin/settings/${encodeURIComponent(tenantId)}/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(value),
    })
  }

  async deleteTenantSetting(tenantId: string, key: string): Promise<{ deleted: boolean }> {
    return this.request(`/admin/settings/${encodeURIComponent(tenantId)}/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    })
  }

  async getGraphIntelligenceRollout(tenantId: string): Promise<GraphIntelligenceRolloutStatus> {
    return this.request<GraphIntelligenceRolloutStatus>(`/admin/graph-intelligence/rollout/${encodeURIComponent(tenantId)}`)
  }

  async updateGraphIntelligenceRollout(
    tenantId: string,
    update: GraphIntelligenceRolloutUpdate,
  ): Promise<GraphIntelligenceRolloutStatus> {
    return this.request<GraphIntelligenceRolloutStatus>(`/admin/graph-intelligence/rollout/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      body: JSON.stringify(update),
    })
  }

  async getAgentConfig(): Promise<{
    provider: string
    model: string
    max_tokens: number
    temperature: number
    max_iterations: number
    timeout_seconds: number
    api_key_configured: boolean
    api_base_url: string
  }> {
    return this.request('/agent/config')
  }

  async updateAgentConfig(updates: {
    provider?: string
    model?: string
    max_tokens?: number
    temperature?: number
    max_iterations?: number
    timeout_seconds?: number
    api_key?: string
    api_base_url?: string
  }): Promise<{
    provider: string
    model: string
    max_tokens: number
    temperature: number
    max_iterations: number
    timeout_seconds: number
    api_key_configured: boolean
    api_base_url: string
  }> {
    return this.request('/agent/config', {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
  }

  // ── LLM Gateway Profiles ────────────────────────────────────────

  async listLLMGateways(tenantId = 'default'): Promise<{ gateways: LLMGatewayProfile[] }> {
    return this.request(`/llm-gateways?tenant_id=${encodeURIComponent(tenantId)}`)
  }

  async upsertLLMGateway(gateway: LLMGatewayUpsert, tenantId = 'default'): Promise<{ ok: boolean; gateway: LLMGatewayProfile }> {
    return this.request(`/llm-gateways?tenant_id=${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      body: JSON.stringify(gateway),
    })
  }

  async deleteLLMGateway(gatewayId: string, tenantId = 'default'): Promise<{ ok: boolean; deleted: string }> {
    return this.request(`/llm-gateways/${encodeURIComponent(gatewayId)}?tenant_id=${encodeURIComponent(tenantId)}`, {
      method: 'DELETE',
    })
  }

  // ── AI Gateway Control Plane ───────────────────────────────────

  async getAIModelCatalog(): Promise<AIModelCatalogDocument> {
    return this.request<AIModelCatalogDocument>('/admin/ai/catalog')
  }

  async upsertAIModelCatalogEntry(alias: string, entry: AIModelCatalogEntry): Promise<AIModelCatalogEntry> {
    return this.request<AIModelCatalogEntry>(`/admin/ai/catalog/${encodeURIComponent(alias)}`, {
      method: 'PUT',
      body: JSON.stringify(entry),
    })
  }

  async deleteAIModelCatalogEntry(alias: string): Promise<{ ok: boolean; deleted: string }> {
    return this.request<{ ok: boolean; deleted: string }>(`/admin/ai/catalog/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
    })
  }

  async getAIModelEntitlements(tenantId: string): Promise<AIModelEntitlements> {
    return this.request<AIModelEntitlements>(`/admin/ai/entitlements/${encodeURIComponent(tenantId)}`)
  }

  async updateAIModelEntitlements(
    tenantId: string,
    entitlements: AIModelEntitlements,
  ): Promise<AIModelEntitlements> {
    return this.request<AIModelEntitlements>(`/admin/ai/entitlements/${encodeURIComponent(tenantId)}`, {
      method: 'PUT',
      body: JSON.stringify(entitlements),
    })
  }

  async getAIUsageSummary(tenantId: string, days = 7): Promise<AIUsageSummary> {
    return this.request<AIUsageSummary>(`/admin/ai/usage/${encodeURIComponent(tenantId)}?days=${days}`)
  }

  async getAIRuntime(): Promise<{
    agent_gateway_enabled: boolean
    agent_gateway_url: string
    agent_gateway_issuer: string
    agent_gateway_audience: string
    agent_gateway_default_model_alias: string
  }> {
    return this.request('/admin/ai/runtime')
  }

  // ── Runtime Control ────────────────────────────────────────────

  async routeRuntimeCapability(body: {
    task_type: string
    preferred_execution_target?: string
    repo_id?: string
    required_toolset?: string[]
    entity_keys?: string[]
    include_handoff_summary?: boolean
  }): Promise<RuntimeRouteResponse> {
    return this.request<RuntimeRouteResponse>('/knowledge/capabilities/route', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async extractRuntimeContextSlice(body: {
    objective: string
    specialist_bundle?: Record<string, unknown>
    fact_ids?: string[]
    prompt_profile?: Record<string, unknown>
    capability?: Record<string, unknown> | null
    repo_id?: string
  }): Promise<RuntimeContextSlice> {
    return this.request<RuntimeContextSlice>('/knowledge/context-slices/extract', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async createRuntimeSubtask(body: {
    contract: Record<string, unknown>
    request_id?: string | null
    session_id?: string
    context?: Record<string, unknown>
    capability_id?: string
  }): Promise<RuntimeSubtaskRecord> {
    return this.request<RuntimeSubtaskRecord>('/agent/subtasks', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getRuntimeSubtaskReplay(subtaskId: string): Promise<RuntimeReplayPayload> {
    return this.request<RuntimeReplayPayload>(`/agent/subtasks/${encodeURIComponent(subtaskId)}/replay`)
  }

  async setRuntimeSubtaskVerdict(
    subtaskId: string,
    body: {
      verdict: string
      reason?: string
      diagnostics?: string[]
      metrics?: Record<string, unknown>
    },
  ): Promise<RuntimeSubtaskRecord> {
    return this.request<RuntimeSubtaskRecord>(`/agent/subtasks/${encodeURIComponent(subtaskId)}/verdict`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async issueRuntimeHandoff(body: {
    subtask_id: string
    recipient_capability_id: string
    context_slice_refs?: string[]
    artifact_refs?: string[]
    policy_version?: string
    expires_in_minutes?: number
  }): Promise<RuntimeHandoffRecord> {
    return this.request<RuntimeHandoffRecord>('/agent/handoffs', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getRuntimeHandoff(handoffId: string): Promise<RuntimeHandoffRecord> {
    return this.request<RuntimeHandoffRecord>(`/agent/handoffs/${encodeURIComponent(handoffId)}`)
  }

  async getRuntimeHandoffLineage(handoffId: string): Promise<Record<string, unknown>> {
    return this.request<Record<string, unknown>>(`/knowledge/handoffs/${encodeURIComponent(handoffId)}/lineage`)
  }

  // ── Tenant Repo Workspace ──────────────────────────────────────

  async listWorkspaceRepos(): Promise<WorkspaceRepoSummary[]> {
    return this.request<WorkspaceRepoSummary[]>('/repos/workspace')
  }

  async createWorkspaceRepo(body: { name: string; description?: string; visibility?: string }): Promise<WorkspaceRepoSummary> {
    return this.request<WorkspaceRepoSummary>('/repos/workspace', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async forkWorkspaceRepo(body: {
    source_url: string
    name: string
    description?: string
    visibility?: string
  }): Promise<WorkspaceRepoSummary> {
    return this.request<WorkspaceRepoSummary>('/repos/workspace/fork', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async importWorkspaceRepo(body: {
    name: string
    description?: string
    visibility?: string
    archive: File
  }): Promise<WorkspaceRepoSummary> {
    const form = new FormData()
    form.set('name', body.name)
    form.set('description', body.description || '')
    form.set('visibility', body.visibility || 'private')
    form.set('archive', body.archive)
    const headers: Record<string, string> = {}
    if (this.token) headers.Authorization = `Bearer ${this.token}`
    const response = await fetch(`${API_BASE}/repos/workspace/import`, {
      method: 'POST',
      headers,
      body: form,
    })
    if (!response.ok) {
      const error = await response.text()
      throw new Error(error || `Request failed: ${response.status}`)
    }
    return response.json() as Promise<WorkspaceRepoSummary>
  }

  async getWorkspaceRepo(repoId: string): Promise<WorkspaceRepoDetail> {
    return this.request<WorkspaceRepoDetail>(`/repos/workspace/${encodeURIComponent(repoId)}`)
  }

  async listWorkspaceCommits(repoId: string, limit = 20): Promise<{ commits: Array<Record<string, unknown>>; count: number }> {
    return this.request<{ commits: Array<Record<string, unknown>>; count: number }>(
      `/repos/workspace/${encodeURIComponent(repoId)}/commits?limit=${limit}`,
    )
  }

  async listWorkspaceTree(repoId: string, ref = 'HEAD', path = ''): Promise<{ entries: GitTreeEntry[] }> {
    return this.request<{ entries: GitTreeEntry[] }>(
      `/repos/workspace/${encodeURIComponent(repoId)}/tree?${new URLSearchParams({ ref, path }).toString()}`,
    )
  }

  async readWorkspaceFile(
    repoId: string,
    filePath: string,
    ref = 'HEAD',
  ): Promise<{ content: string; path: string; size: number; truncated: boolean }> {
    return this.request<{ content: string; path: string; size: number; truncated: boolean }>(
      `/repos/workspace/${encodeURIComponent(repoId)}/file?${new URLSearchParams({ ref, path: filePath }).toString()}`,
    )
  }

  async diffWorkspaceRepo(repoId: string, base: string, head: string): Promise<{ diff: string }> {
    return this.request<{ diff: string }>(
      `/repos/workspace/${encodeURIComponent(repoId)}/diff?${new URLSearchParams({ base, head }).toString()}`,
    )
  }

  async createWorkspaceBranch(
    repoId: string,
    body: { branch_name: string; from_ref: string },
  ): Promise<{ ok: boolean; branch: string; commit?: string }> {
    return this.request<{ ok: boolean; branch: string; commit?: string }>(
      `/repos/workspace/${encodeURIComponent(repoId)}/branches`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  }

  async listWorkspacePullRequests(repoId: string): Promise<WorkspacePullRequest[]> {
    return this.request<WorkspacePullRequest[]>(`/repos/workspace/${encodeURIComponent(repoId)}/pull-requests`)
  }

  async createWorkspacePullRequest(
    repoId: string,
    body: { title: string; description?: string; source_branch: string; target_branch: string },
  ): Promise<WorkspacePullRequest> {
    return this.request<WorkspacePullRequest>(`/repos/workspace/${encodeURIComponent(repoId)}/pull-requests`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async mergeWorkspacePullRequest(repoId: string, prId: string): Promise<WorkspacePullRequest> {
    return this.request<WorkspacePullRequest>(
      `/repos/workspace/${encodeURIComponent(repoId)}/pull-requests/${encodeURIComponent(prId)}/merge`,
      { method: 'POST' },
    )
  }

  async listWorkspaceSshKeys(): Promise<WorkspaceSshKey[]> {
    return this.request<WorkspaceSshKey[]>('/repos/workspace/ssh-keys')
  }

  async createWorkspaceSshKey(body: { title: string; public_key: string }): Promise<WorkspaceSshKey> {
    return this.request<WorkspaceSshKey>('/repos/workspace/ssh-keys', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async deleteWorkspaceSshKey(keyId: string): Promise<void> {
    return this.request<void>(`/repos/workspace/ssh-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    })
  }

  async exportWorkspaceAuthorizedKeys(): Promise<{ authorized_keys: string; count: number }> {
    return this.request<{ authorized_keys: string; count: number }>('/repos/workspace/ssh-keys/export')
  }

  // ── Unified Search ──────────────────────────────────────────────

  async unifiedSearch(
    query: string,
    projectId: string,
    options?: { topK?: number; sources?: ('knowledge' | 'datasource')[] }
  ): Promise<UnifiedSearchResult> {
    return this.request('/search/unified', {
      method: 'POST',
      body: JSON.stringify({
        query,
        project_id: projectId,
        top_k: options?.topK ?? 10,
        sources: options?.sources ?? ['knowledge', 'datasource'],
      }),
    })
  }

  async embedAssetChunks(assetId: string): Promise<{ embedded: number; asset_id: string }> {
    return this.request(`/datasources/assets/${assetId}/embed`, {
      method: 'POST',
    })
  }

  // ── Tool helper (unwraps {tool, result} envelope) ──────────────

  private async toolRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const data = await this.request<{ tool: string; result: T }>(endpoint, options)
    return data.result
  }

  // ── Git: Code Browsing ──────────────────────────────────────────

  async gitListRepos(): Promise<{ repositories: string[]; count: number }> {
    return this.request('/org/repos')
  }

  async getOrgContext(): Promise<{
    tenant: { tenant_id: string; name: string } | null
    organizations: { org_id: string; name: string; tenant_id: string }[]
    teams: { team_id: string; name: string; org_id: string }[]
  }> {
    return this.request('/org/context')
  }

  async gitListTree(
    name: string,
    ref = 'HEAD',
    path = '',
  ): Promise<{ entries: GitTreeEntry[] }> {
    return this.toolRequest('/tools/git/list_tree', {
      method: 'POST',
      body: JSON.stringify({ params: { name, ref, path } }),
    })
  }

  async gitReadFile(
    name: string,
    path: string,
    ref = 'HEAD',
  ): Promise<{ content: string; path: string; size: number; truncated: boolean }> {
    return this.toolRequest('/tools/git/read_file', {
      method: 'POST',
      body: JSON.stringify({ params: { name, path, ref } }),
    })
  }

  async gitSearchFiles(
    name: string,
    query: string,
    ref = 'HEAD',
    limit = 50,
  ): Promise<{ entries: GitTreeEntry[]; count: number }> {
    return this.toolRequest('/tools/git/search_files', {
      method: 'POST',
      body: JSON.stringify({ params: { name, query, ref, limit } }),
    })
  }

  async gitListBranches(repoName: string): Promise<{ name: string; branches: string[]; count: number }> {
    return this.toolRequest('/tools/git/list_branches', {
      method: 'POST',
      body: JSON.stringify({ params: { name: repoName } }),
    })
  }

  async createBranch(
    repoName: string,
    branchName: string,
    fromRef = 'main',
  ): Promise<{ ok: boolean; branch: string; commit: string }> {
    return this.toolRequest('/tools/git/create_branch', {
      method: 'POST',
      body: JSON.stringify({
        params: { repo_name: repoName, branch_name: branchName, from_ref: fromRef },
      }),
    })
  }

  async deleteBranch(repoName: string, branchName: string): Promise<{ ok: boolean }> {
    return this.toolRequest('/tools/git/delete_branch', {
      method: 'POST',
      body: JSON.stringify({ params: { repo_name: repoName, branch_name: branchName } }),
    })
  }

  // ── Knowledge: Annotations ─────────────────────────────────────

  async draftWorkItemsFromSpec(body: {
    project_id: string
    spec_id: string
    parent_epic_id?: string
  }): Promise<{ drafts: WorkItemDraft[] }> {
    return this.request('/workitems/draft-from-spec', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async acceptWorkItemDrafts(body: {
    project_id: string
    drafts: WorkItemDraft[]
  }): Promise<{ ok: boolean; items: WorkItemData[] }> {
    return this.request('/workitems/accept-drafts', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async linkNodeToAsset(body: {
    node_id: string
    asset_id: string
    project_id: string
    link_type?: string
  }): Promise<{ ok: boolean; link: Record<string, unknown> }> {
    return this.request('/knowledge/link-asset', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async unlinkNodeFromAsset(body: {
    node_id: string
    asset_id: string
    project_id: string
  }): Promise<{ ok: boolean }> {
    return this.request('/knowledge/unlink-asset', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getLinkedAssets(body: {
    node_id: string
    project_id: string
  }): Promise<{ links: { node_id: string; asset_id: string; link_type: string }[] }> {
    return this.request('/knowledge/linked-assets', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getRelatedItems(body: {
    project_id: string
    repo?: string
    file?: string
    tags?: string[]
  }): Promise<RelatedItemsResult> {
    return this.request('/knowledge/related-items', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async createWorkItem(body: {
    project_id: string
    level: string
    title: string
    description?: string
    parent_id?: string
    persona?: string
    linked_node_ids?: string[]
    tags?: string[]
  }): Promise<{ ok: boolean; item: WorkItemData }> {
    return this.request('/workitems/create', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listWorkItems(body: {
    project_id: string
    level?: string
    parent_id?: string
  }): Promise<{ items: WorkItemData[] }> {
    return this.request('/workitems/list', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getWorkItemHierarchy(body: {
    project_id: string
  }): Promise<{ tree: WorkItemData[] }> {
    return this.request('/workitems/hierarchy', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async updateWorkItem(itemId: string, body: {
    project_id: string
    title?: string
    description?: string
    status?: string
    linked_node_ids?: string[]
  }): Promise<{ ok: boolean; item: WorkItemData }> {
    return this.request(`/workitems/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  async addComment(body: {
    node_id: string
    project_id: string
    content: string
    persona?: string
    parent_comment_id?: string
  }): Promise<{ ok: boolean; comment: AnnotationCommentData }> {
    return this.request('/knowledge/comments', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listComments(body: {
    node_id: string
    project_id: string
  }): Promise<{ comments: AnnotationCommentData[] }> {
    return this.request('/knowledge/comments/list', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async updateAnnotationStatus(body: {
    node_id: string
    project_id: string
    status: string
    persona?: string
  }): Promise<{ ok: boolean; status: string }> {
    return this.request('/knowledge/annotation-status', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listActivity(body: {
    project_id: string
    limit?: number
  }): Promise<{ events: ActivityEventData[] }> {
    return this.request('/knowledge/activity', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async getHandoffSummary(body: {
    project_id: string
    from_persona: string
    to_persona: string
  }): Promise<HandoffSummary> {
    return this.request('/knowledge/handoff', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async createAnnotation(body: {
    project_id: string
    content: string
    repo: string
    file: string
    line_start: number
    line_end: number
    title?: string
    tags?: string[]
    symbol?: string
    linked_knowledge_ids?: string[]
    persona?: string
  }): Promise<{ ok: boolean; node: KnowledgeNodeData }> {
    return this.request('/knowledge/annotations', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listAnnotations(body: {
    project_id: string
    repo?: string
    file?: string
    tag?: string
    limit?: number
  }): Promise<{ annotations: KnowledgeNodeData[] }> {
    return this.request('/knowledge/annotations/list', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  // ── Specs ──────────────────────────────────────────────────────

  async listSpecTemplates(): Promise<{ templates: SpecTemplateData[] }> {
    return this.request('/specs/templates')
  }

  async createSpec(body: {
    project_id: string
    template_id?: string
    title?: string
  }): Promise<{ spec: SpecInstanceData; sections: KnowledgeNodeData[] }> {
    return this.request('/specs/create', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listSpecs(projectId: string): Promise<{ specs: SpecListItem[] }> {
    return this.request('/specs/list', {
      method: 'POST',
      body: JSON.stringify({ project_id: projectId }),
    })
  }

  async getSpecSection(nodeId: string, projectId: string): Promise<{ section: KnowledgeNodeData }> {
    return this.request(`/specs/section/${nodeId}?project_id=${encodeURIComponent(projectId)}`)
  }

  async updateSpecSection(body: {
    project_id: string
    node_id: string
    content: string
    title?: string
  }): Promise<{ ok: boolean; section: KnowledgeNodeData }> {
    return this.request('/specs/section', {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  }

  async snapshotSpec(body: {
    project_id: string
    spec_id: string
    label?: string
  }): Promise<SpecSnapshotData> {
    return this.request('/specs/snapshot', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async listSnapshots(body: {
    project_id: string
    spec_id: string
  }): Promise<{ snapshots: SpecSnapshotSummary[] }> {
    return this.request('/specs/snapshots', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async diffSnapshots(body: {
    project_id: string
    spec_id: string
    snapshot_id_a?: string
    snapshot_id_b?: string
  }): Promise<{ diffs: SpecDiffEntry[] }> {
    return this.request('/specs/diff', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  }

  async exportSpecMarkdown(specId: string, projectId: string): Promise<string> {
    const headers: Record<string, string> = {}
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`
    const resp = await fetch(`${API_BASE}/specs/export/${specId}?project_id=${encodeURIComponent(projectId)}`, {
      headers,
    })
    return resp.text()
  }

  async trackUxEvent(event: UxEventInput): Promise<void> {
    try {
      await this.request('/eval/ux/events', {
        method: 'POST',
        body: JSON.stringify(event),
      })
    } catch {
      // UX telemetry must not block user workflows.
    }
  }

  private websocketUrl(): string {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const url = new URL(`${protocol}//${window.location.host}${API_BASE}/agent/ws`)
    if (this.token) {
      url.searchParams.set('token', this.token)
    }
    return url.toString()
  }

  private async ensureWebSocket(): Promise<WebSocket> {
    const epoch = this.authEpoch
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return this.ws
    }
    if (this.wsReady) {
      await this.wsReady
      if (epoch !== this.authEpoch) throw new Error('Account changed')
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        return this.ws
      }
    }

    this.wsReady = new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(this.websocketUrl())
      this.ws = ws

      ws.onopen = () => {
        if (epoch !== this.authEpoch) { ws.close(); reject(new Error('Account changed')); return }
        this.ws = ws
        resolve()
      }

      ws.onmessage = (event) => {
        if (epoch !== this.authEpoch || this.ws !== ws) return
        try {
          const parsed = parseAgentServerFrame(JSON.parse(String(event.data)))
          if (!parsed) {
            return
          }
          if (parsed.type === 'pong' || parsed.type === 'ack') {
            return
          }
          if (parsed.type === 'event') {
            this.updateListeners.forEach(listener => listener(parsed))
            return
          }
          if (parsed.type === 'confirmation_required') {
            this.confirmationListeners.forEach(listener => listener(parsed))
            return
          }
          if (parsed.type === 'updates') {
            const requestId = parsed.request_id
            const pending = this.pendingUpdates.get(requestId)
            if (!pending) {
              return
            }
            pending.resolve(parsed.events)
            this.pendingUpdates.delete(requestId)
            return
          }
          const requestId = parsed.request_id
          if (!requestId) {
            return
          }
          const pending = this.pending.get(requestId)
          if (!pending) {
            return
          }
          if (parsed.type === 'final') {
            pending.resolve({
              session_id: parsed.session_id || '',
              response: parsed.response || '',
              response_format: parsed.response_format || 'markdown',
              render_hints: parsed.render_hints || {},
              tool_calls: this.normalizeToolCalls(parsed.tool_calls),
              context_bundle: parsed.context_bundle ?? null,
              citations: parsed.citations,
            })
            this.pending.delete(requestId)
            return
          }
          if (parsed.type === 'error') {
            pending.reject(new Error(parsed.detail || 'Request failed'))
            this.pending.delete(requestId)
          }
        } catch {
          // Ignore non-JSON frames.
        }
      }

      ws.onerror = () => {
        reject(new Error('WebSocket error'))
      }

      ws.onclose = () => {
        reject(new Error('WebSocket closed'))
        if (epoch !== this.authEpoch || this.ws !== ws) return
        this.ws = null
        this.wsReady = null
        for (const [, pending] of this.pending) {
          pending.reject(new Error('WebSocket closed'))
        }
        this.pending.clear()
        for (const [, pending] of this.pendingUpdates) {
          pending.reject(new Error('WebSocket closed'))
        }
        this.pendingUpdates.clear()
      }
    })

    await this.wsReady
    if (epoch !== this.authEpoch) throw new Error('Account changed')
    return this.ws as WebSocket
  }

  private async chatViaWebSocket(
    message: string,
    sessionId?: string,
    intentContext?: ChatIntentContext,
    tier?: string | null,
    projectId?: string,
    persona?: string,
  ): Promise<ChatResponse> {
    const epoch = this.authEpoch
    const ws = await this.ensureWebSocket()
    if (epoch !== this.authEpoch) throw new Error('Account changed')
    const requestId = this.makeId()

    return new Promise<ChatResponse>((resolve, reject) => {
      this.pending.set(requestId, { resolve, reject })
      ws.send(
        JSON.stringify({
          type: 'chat',
          request_id: requestId,
          message,
          session_id: sessionId,
          context: {
            ...(intentContext ? { intent_context: intentContext } : {}),
            ...(projectId ? { project_id: projectId } : {}),
            ...(persona ? { persona } : {}),
          },
          ...(tier ? { tier } : {}),
        })
      )
    })
  }

  async getProgressUpdates(requestId: string): Promise<AgentProgressEvent[]> {
    const ws = await this.ensureWebSocket()
    return new Promise<AgentProgressEvent[]>((resolve, reject) => {
      this.pendingUpdates.set(requestId, { resolve, reject })
      ws.send(
        JSON.stringify({
          type: 'get_updates',
          request_id: requestId
        })
      )
    })
  }

  // ── Projects ─────────────────────────────────────────────────────────────

  async listProjects(orgId?: string): Promise<{ projects: ProjectData[]; count: number }> {
    const qs = orgId ? `?${new URLSearchParams({ org_id: orgId })}` : ''
    return this.request<{ projects: ProjectData[]; count: number }>(`/projects${qs}`)
  }

  async createProject(data: {
    name: string
    description?: string
    org_id?: string
    settings?: Record<string, unknown>
  }): Promise<ProjectData> {
    return this.request<ProjectData>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getProject(projectId: string): Promise<ProjectData> {
    return this.request<ProjectData>(`/projects/${projectId}`)
  }

  async updateProject(
    projectId: string,
    data: { name?: string; description?: string; settings?: Record<string, unknown> },
  ): Promise<ProjectData> {
    return this.request<ProjectData>(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteProject(projectId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/projects/${projectId}`, {
      method: 'DELETE',
    })
  }

  async linkProjectRepo(projectId: string, repoName: string): Promise<{ linked: boolean; repo_name: string }> {
    return this.request<{ linked: boolean; repo_name: string }>(`/projects/${projectId}/repos`, {
      method: 'POST',
      body: JSON.stringify({ repo_name: repoName }),
    })
  }

  async unlinkProjectRepo(projectId: string, repoName: string): Promise<{ unlinked: boolean }> {
    return this.request<{ unlinked: boolean }>(`/projects/${projectId}/repos/${encodeURIComponent(repoName)}`, {
      method: 'DELETE',
    })
  }

  async addProjectMember(projectId: string, userId: string, role?: string): Promise<{ added: boolean; user_id: string; role: string }> {
    return this.request<{ added: boolean; user_id: string; role: string }>(`/projects/${projectId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, ...(role ? { role } : {}) }),
    })
  }

  async removeProjectMember(projectId: string, userId: string): Promise<{ removed: boolean }> {
    return this.request<{ removed: boolean }>(`/projects/${projectId}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    })
  }

  // ── Conversations (Phase 0.5) ───────────────────────────────────────────

  async listConversations(projectId?: string): Promise<{ conversations: ConversationData[]; count: number }> {
    const qs = projectId ? `?${new URLSearchParams({ project_id: projectId })}` : ''
    return this.request<{ conversations: ConversationData[]; count: number }>(`/conversations${qs}`)
  }

  async createConversation(data: {
    project_id: string
    title?: string
    persona_id?: string
  }): Promise<ConversationData> {
    return this.request<ConversationData>('/conversations', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async getConversation(conversationId: string): Promise<ConversationData> {
    return this.request<ConversationData>(`/conversations/${conversationId}`)
  }

  async updateConversation(conversationId: string, data: { title?: string }): Promise<ConversationData> {
    return this.request<ConversationData>(`/conversations/${conversationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteConversation(conversationId: string): Promise<{ deleted: boolean }> {
    return this.request<{ deleted: boolean }>(`/conversations/${conversationId}`, {
      method: 'DELETE',
    })
  }

  async appendMessage(
    conversationId: string,
    message: { role: 'user' | 'assistant' | 'system'; content: string; metadata?: Record<string, unknown> },
  ): Promise<MessageData> {
    return this.request<MessageData>(`/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify(message),
    })
  }

  async listMessages(
    conversationId: string,
    limit?: number,
    before?: string,
  ): Promise<{ messages: MessageData[]; count: number }> {
    const params: Record<string, string> = {}
    if (limit != null) params.limit = String(limit)
    if (before) params.before = before
    const qs = Object.keys(params).length ? `?${new URLSearchParams(params)}` : ''
    return this.request<{ messages: MessageData[]; count: number }>(
      `/conversations/${conversationId}/messages${qs}`,
    )
  }
}

// -- Skill API functions (standalone, not on the class) ---------------------

export interface SkillCatalogEntry {
  id: string
  name: string
  description: string
  plugin: string
  scope: string
  persona_affinity: string[]
}

export interface CommandCatalogEntry {
  name: string
  description: string
  plugin: string
  step_count: number
  persona_affinity: string[]
  examples?: string[]
  category?: string
}

export interface SkillCatalog {
  skills: SkillCatalogEntry[]
  commands: CommandCatalogEntry[]
  total_skills: number
  total_commands: number
}

export interface SkillInstallResult {
  installed: number
  source_url: string
  skills: { id: string; name: string; plugin: string }[]
}

export async function fetchSkillCatalog(persona?: string, plugin?: string): Promise<SkillCatalog> {
  const params = new URLSearchParams()
  if (persona) params.set('persona', persona)
  if (plugin) params.set('plugin', plugin)
  const qs = params.toString()
  const res = await fetch(`${API_BASE}/skills/catalog${qs ? '?' + qs : ''}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to fetch skill catalog: ${res.status}`)
  return res.json()
}

export async function fetchCommands(persona?: string): Promise<CommandCatalogEntry[]> {
  const params = new URLSearchParams()
  if (persona) params.set('persona', persona)
  const qs = params.toString()
  const res = await fetch(`${API_BASE}/skills/commands${qs ? '?' + qs : ''}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to fetch commands: ${res.status}`)
  const data = await res.json()
  return data.commands
}

export async function installSkillsFromUrl(url: string, personaAffinity?: string[]): Promise<SkillInstallResult> {
  const res = await fetch(`${API_BASE}/skills/install`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, persona_affinity: personaAffinity }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `Installation failed: ${res.status}`)
  }
  return res.json()
}

export async function uninstallSkill(skillId: string): Promise<void> {
  const res = await fetch(`${API_BASE}/skills/${encodeURIComponent(skillId)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to uninstall skill: ${res.status}`)
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token') || ''
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export const api = new ApiService()
export default api
